import { useState, useEffect, useCallback, useRef } from "react";
import { 
  connectQZTray, 
  isQZConnected, 
  isQZWebSocketActive,
  registerConnectionCallbacks,
  setAutoReconnectEnabled,
  getAutoReconnectEnabled
} from "@/lib/qz-tray";
import { checkAndNotifyStalePrintJobs, getPendingPrintCount } from "@/lib/print-queue";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useCentralizedPrintQueue } from "@/hooks/useCentralizedPrintQueue";

const RECONNECT_CHECK_INTERVAL = 10000; // 10 seconds (was 15s)
const HEARTBEAT_INTERVAL = 30000; // 30 seconds - detect silent disconnects
const IMMEDIATE_RECONNECT_DELAY = 2000; // 2 seconds after close callback
const STALE_CHECK_INTERVAL = 60000; // 1 minute
const LOCAL_STORAGE_KEY = "qz_auto_reconnect_enabled";

export function useQZTrayConnection() {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [autoReconnect, setAutoReconnect] = useState(() => {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  });
  const reconnectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const staleCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const immediateReconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const autoReconnectRef = useRef(autoReconnect);
  const { activeRestaurant } = useRestaurantContext();

  // Keep ref in sync for use in callbacks
  useEffect(() => {
    autoReconnectRef.current = autoReconnect;
  }, [autoReconnect]);

  useEffect(() => {
    isConnectingRef.current = isConnecting;
  }, [isConnecting]);

  // Enable centralized print queue processing when connected
  useCentralizedPrintQueue(isConnected);

  // Update connection state
  const updateConnectionState = useCallback(() => {
    const connected = isQZWebSocketActive();
    setIsConnected(connected);
    return connected;
  }, []);

  // Handle connection
  const connect = useCallback(async () => {
    if (isConnectingRef.current) return false;
    
    setIsConnecting(true);
    isConnectingRef.current = true;
    try {
      const success = await connectQZTray();
      if (isMountedRef.current) {
        setIsConnected(success);
      }
      return success;
    } finally {
      if (isMountedRef.current) {
        setIsConnecting(false);
        isConnectingRef.current = false;
      }
    }
  }, []);

  // Immediate reconnect helper (used by close/error callbacks)
  const scheduleImmediateReconnect = useCallback(() => {
    if (immediateReconnectRef.current) {
      clearTimeout(immediateReconnectRef.current);
    }
    immediateReconnectRef.current = setTimeout(() => {
      if (autoReconnectRef.current && !isConnectingRef.current && !isQZWebSocketActive()) {
        console.log("[QZ] Immediate reconnect after disconnect...");
        connect();
      }
    }, IMMEDIATE_RECONNECT_DELAY);
  }, [connect]);

  // Toggle auto-reconnect
  const toggleAutoReconnect = useCallback((enabled: boolean) => {
    setAutoReconnect(enabled);
    localStorage.setItem(LOCAL_STORAGE_KEY, enabled ? "true" : "false");
    setAutoReconnectEnabled(enabled);
    
    if (enabled && !isQZWebSocketActive()) {
      connect();
    }
  }, [connect]);

  // Always attempt initial connection on mount (once), regardless of autoReconnect
  useEffect(() => {
    if (!isQZWebSocketActive() && !isConnectingRef.current) {
      console.log("[QZ] Initial connection attempt on mount...");
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setup periodic reconnect monitor (only when autoReconnect is enabled)
  useEffect(() => {
    if (!autoReconnect) {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
      return;
    }

    // Setup periodic reconnection check
    reconnectIntervalRef.current = setInterval(() => {
      if (!isQZWebSocketActive() && autoReconnectRef.current && !isConnectingRef.current) {
        console.log("[QZ] Auto-reconnect: tentando reconectar...");
        connect();
      }
    }, RECONNECT_CHECK_INTERVAL);

    return () => {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
    };
  }, [autoReconnect, connect]);

  // Heartbeat: detect silent WebSocket deaths every 30s
  useEffect(() => {
    heartbeatIntervalRef.current = setInterval(() => {
      const socketActive = isQZWebSocketActive();
      if (isMountedRef.current) {
        setIsConnected(socketActive);
      }
      // If socket silently died and auto-reconnect is on, trigger reconnect
      if (!socketActive && autoReconnectRef.current && !isConnectingRef.current) {
        console.log("[QZ] Heartbeat detected dead socket, reconnecting...");
        connect();
      }
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [connect]);

  // Stale print jobs monitoring - send push notification when jobs are pending > 5 min
  useEffect(() => {
    if (!activeRestaurant?.id) {
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
        staleCheckIntervalRef.current = null;
      }
      return;
    }
    
    const checkStaleJobs = () => {
      const pendingCount = getPendingPrintCount();
      if (pendingCount > 0) {
        checkAndNotifyStalePrintJobs(activeRestaurant.id);
      }
    };

    const initialTimeout = setTimeout(checkStaleJobs, 10000);
    staleCheckIntervalRef.current = setInterval(checkStaleJobs, STALE_CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
        staleCheckIntervalRef.current = null;
      }
    };
  }, [activeRestaurant?.id]);

  // Register callbacks for connection state changes + immediate reconnect on close
  useEffect(() => {
    const handleClosed = () => {
      console.log("[QZ] Connection closed callback");
      if (isMountedRef.current) {
        setIsConnected(false);
      }
      // Schedule immediate reconnect if auto-reconnect is enabled
      scheduleImmediateReconnect();
    };

    const handleError = (error: Error) => {
      console.error("[QZ] Connection error callback:", error);
      if (isMountedRef.current) {
        setIsConnected(false);
      }
      // Also try immediate reconnect on error
      scheduleImmediateReconnect();
    };

    registerConnectionCallbacks(handleClosed, handleError);

    // Initial state check
    updateConnectionState();

    return () => {
      isMountedRef.current = false;
      if (immediateReconnectRef.current) {
        clearTimeout(immediateReconnectRef.current);
      }
    };
  }, [updateConnectionState, scheduleImmediateReconnect]);

  // Sync with global state
  useEffect(() => {
    setAutoReconnectEnabled(autoReconnect);
  }, [autoReconnect]);

  return {
    isConnected,
    isConnecting,
    autoReconnect,
    connect,
    toggleAutoReconnect,
    updateConnectionState
  };
}
